import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Typography } from "../../../components";
import { useTranslations } from "../../../contexts/translation.context";
import { FiltersSection, OnlyFansAccountsTable, AddHeadersModal } from "./components";
import staffApi, { OnlyFansAccount, GetOnlyFansAccountsParams, ModelHeaders } from "../../../api/staff";
import { showPartnerSuccess, showError } from "../../../utils/toast";
import { usePageBreadcrumbs } from "../../../hooks/usePageBreadcrumbs";

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_DELAY = 500;

export interface OnlyFansFilters {
  search: string;
  status: string;
  ownerType: string;
}

const DEFAULT_FILTERS: OnlyFansFilters = {
  search: '',
  status: 'all',
  ownerType: 'all',
};

/**
 * OnlyFans Accounts Page Component
 *
 * This is the main page component for managing OnlyFans accounts in the admin panel.
 * It follows the unified component pattern for onlyu_admin where API integration,
 * state management, and UI are combined in a single cohesive component.
 *
 * Architecture:
 * - Uses staffApi.getOnlyFansAccounts() from the unified API client
 * - Manages all state locally (no separate context needed)
 * - Integrates filtering, pagination, and search functionality
 * - Follows the pattern established in onlyu_front but adapted for single-client architecture
 */
const OnlyFansAccountsPage: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.onlyfansAccounts'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // State management
  const [accounts, setAccounts] = useState<OnlyFansAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<OnlyFansFilters>(DEFAULT_FILTERS);

  // Local state for debounced search input
  const [localSearch, setLocalSearch] = useState(filters.search);

  // Modal state for adding headers
  const [isHeadersModalOpen, setIsHeadersModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string; ofUserId: string } | null>(null);
  const [existingHeaders, setExistingHeaders] = useState<string | null>(null);
  const [isLoadingHeaders, setIsLoadingHeaders] = useState(false);

  // Debounce for search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Sync local search with filters when they change externally
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Prepare API params
  const apiParams = useMemo(() => {
    const params: GetOnlyFansAccountsParams = {
      page: currentPage,
      page_size: pageSize,
    };

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.status !== 'all') {
      params.status = filters.status as any;
    }

    if (filters.ownerType !== 'all') {
      params.owner_type = filters.ownerType as 'creator' | 'agency';
    }

    return params;
  }, [currentPage, pageSize, filters.search, filters.status, filters.ownerType]);

  // Load accounts when dependencies change
  useEffect(() => {
    const loadAccounts = async () => {
      setIsLoading(true);
      try {
        const response = await staffApi.getOnlyFansAccounts(apiParams);

        if (response && response.objects && Array.isArray(response.objects)) {
          setAccounts(response.objects);
          setTotalCount(response.count || 0);
        } else {
          setAccounts([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Failed to load OnlyFans accounts:', error);
        setAccounts([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadAccounts();
  }, [apiParams]);

  const handleFilterChange = useCallback((key: keyof OnlyFansFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const handleActivate = useCallback(async (accountId: string) => {
    try {
      await staffApi.activateOnlyFansAccount(accountId);
      // Reload accounts after activation
      const response = await staffApi.getOnlyFansAccounts(apiParams);
      if (response && response.objects && Array.isArray(response.objects)) {
        setAccounts(response.objects);
        setTotalCount(response.count || 0);
      }
      showPartnerSuccess('Аккаунт успешно активирован');
    } catch (error: any) {
      console.error('Failed to activate account:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Не удалось активировать аккаунт';
      showError(errorMessage);
    }
  }, [apiParams]);

  const handleDeactivate = useCallback(async (accountId: string) => {
    try {
      await staffApi.deactivateOnlyFansAccount(accountId);
      // Reload accounts after deactivation
      const response = await staffApi.getOnlyFansAccounts(apiParams);
      if (response && response.objects && Array.isArray(response.objects)) {
        setAccounts(response.objects);
        setTotalCount(response.count || 0);
      }
      showPartnerSuccess('Аккаунт успешно деактивирован');
    } catch (error: any) {
      console.error('Failed to deactivate account:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Не удалось деактивировать аккаунт';
      showError(errorMessage);
    }
  }, [apiParams]);

  const handleVerify = useCallback(async (accountId: string) => {
    try {
      await staffApi.verifyOnlyFansAccount(accountId);
      // Reload accounts after verification
      const response = await staffApi.getOnlyFansAccounts(apiParams);
      if (response && response.objects && Array.isArray(response.objects)) {
        setAccounts(response.objects);
        setTotalCount(response.count || 0);
      }
      showPartnerSuccess('Аккаунт успешно верифицирован');
    } catch (error: any) {
      console.error('Failed to verify account:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Не удалось верифицировать аккаунт';
      showError(errorMessage);
    }
  }, [apiParams]);

  const handleAddHeaders = useCallback(async (accountId: string, accountName: string, ofUserId: string) => {
    setSelectedAccount({ id: accountId, name: accountName, ofUserId });
    setIsLoadingHeaders(true);

    try {
      // Проверяем наличие существующих хедеров
      const headers = await staffApi.getModelHeaders(accountId, ofUserId);
      setExistingHeaders(headers);
    } catch (error) {
      console.error('Failed to load existing headers:', error);
      setExistingHeaders(null);
    } finally {
      setIsLoadingHeaders(false);
      setIsHeadersModalOpen(true);
    }
  }, []);

  const handleSubmitHeaders = useCallback(async (headers: ModelHeaders) => {
    if (!selectedAccount) return;

    try {
      // Если хедеры уже существуют, используем PATCH, иначе POST
      if (existingHeaders) {
        await staffApi.updateModelHeaders(selectedAccount.id, headers);
        showPartnerSuccess('Headers успешно обновлены');
      } else {
        await staffApi.addModelHeaders(selectedAccount.id, headers);
        showPartnerSuccess('Headers успешно добавлены');
      }
    } catch (error: any) {
      console.error('Failed to add headers:', error);

      // Extract error message from various possible formats
      let errorMessage = 'Не удалось добавить headers';

      if (error?.response?.data) {
        const data = error.response.data;

        // Check if detail is an array with validation errors
        if (Array.isArray(data.detail)) {
          const validationErrors = data.detail
            .map((err: any) => {
              if (err.msg && err.loc) {
                const field = Array.isArray(err.loc) ? err.loc.join('.') : err.loc;
                return `${field}: ${err.msg}`;
              }
              return err.msg || JSON.stringify(err);
            })
            .join('; ');
          errorMessage = validationErrors || errorMessage;
        }
        // Check if detail/details is a string
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        else if (typeof data.details === 'string') {
          errorMessage = data.details;
        }
        // Check for message field
        else if (typeof data.message === 'string') {
          errorMessage = data.message;
        }
        // If data itself is a string
        else if (typeof data === 'string') {
          errorMessage = data;
        }
      }
      // Fallback to error.message
      else if (error?.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
      throw error;
    }
  }, [selectedAccount, existingHeaders]);

  // Check if there are active filters
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.status !== 'all' ||
      filters.ownerType !== 'all'
    );
  }, [filters]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Page Header */}
      <div className="py-[16px] px-[24px]">
        <Typography variant="h5" className="font-semibold text-gray-900">
          {t('admin.onlyfans.title')}
        </Typography>
      </div>

      {/* Filters Section */}
      <FiltersSection
        hasActiveFilters={hasActiveFilters}
        filters={filters}
        localSearch={localSearch}
        onSearchChange={setLocalSearch}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
      />

      {/* Table */}
      <div className="px-[24px] pb-[24px]">
        <OnlyFansAccountsTable
          accounts={accounts}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          onVerify={handleVerify}
          onAddHeaders={handleAddHeaders}
        />
      </div>

      {/* Add Headers Modal */}
      <AddHeadersModal
        isOpen={isHeadersModalOpen}
        onClose={() => setIsHeadersModalOpen(false)}
        onSubmit={handleSubmitHeaders}
        accountName={selectedAccount?.name || ''}
        existingHeaders={existingHeaders}
      />
    </div>
  );
};

export default OnlyFansAccountsPage;
